import { useEffect, useMemo, useState, useCallback } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { api } from "@/lib/api";
import { errMsg } from "@/lib/errors";
import { toast } from "sonner";
import { Search, Upload, Plus, Image as ImageIcon, X, Library, Tag } from "lucide-react";

/**
 * Reusable photo picker. Used wherever the admin needs to choose images
 * (rooms, gallery, services, attractions, ...).
 *
 * Props:
 *  - open: boolean
 *  - onClose: () => void
 *  - onSelect: (url: string | string[]) => void   // returns selected URL(s)
 *  - multiple: boolean (default false)
 *  - defaultCategory: string (suggested tag/category for new uploads)
 *  - title: string (dialog title, optional)
 */
export default function PhotoPicker({ open, onClose, onSelect, multiple = false, defaultCategory = "", title }) {
  const [tab, setTab] = useState("library"); // library | upload
  const [items, setItems] = useState([]);
  const [tags, setTags] = useState([]);
  const [categories, setCategories] = useState([]);
  const [q, setQ] = useState("");
  const [activeTag, setActiveTag] = useState("");
  const [activeCategory, setActiveCategory] = useState("");
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(new Set());

  // Upload tab state
  const [uploadTags, setUploadTags] = useState(defaultCategory || "");
  const [uploadCategory, setUploadCategory] = useState(defaultCategory || "");
  const [uploadTitle, setUploadTitle] = useState("");
  const [uploading, setUploading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (q.trim()) params.q = q.trim();
      if (activeTag) params.tag = activeTag;
      if (activeCategory) params.category = activeCategory;
      const [libRes, tagsRes] = await Promise.all([
        api.get("/admin/photo-library", { params }),
        api.get("/admin/photo-library/tags"),
      ]);
      setItems(libRes.data?.items || []);
      setTags(tagsRes.data?.tags || []);
      setCategories(tagsRes.data?.categories || []);
    } catch (e) {
      toast.error("Impossibile caricare l'archivio foto");
    } finally {
      setLoading(false);
    }
  }, [q, activeTag, activeCategory]);

  useEffect(() => {
    if (!open) return;
    setSelected(new Set());
    setTab("library");
    load();
    // eslint-disable-next-line
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [q, activeTag, activeCategory, open, load]);

  const toggleSelect = (url) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (multiple) {
        if (next.has(url)) next.delete(url);
        else next.add(url);
      } else {
        next.clear();
        next.add(url);
      }
      return next;
    });
  };

  const confirm = () => {
    if (selected.size === 0) {
      toast.error("Seleziona almeno una foto");
      return;
    }
    const arr = Array.from(selected);
    onSelect(multiple ? arr : arr[0]);
    onClose();
  };

  const handleUpload = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const uploaded = [];
    for (const file of files) {
      try {
        const fd = new FormData();
        fd.append("file", file);
        if (uploadTags.trim()) fd.append("tags", uploadTags.trim());
        if (uploadCategory.trim()) fd.append("category", uploadCategory.trim());
        if (uploadTitle.trim()) fd.append("title", uploadTitle.trim());
        const res = await api.post("/admin/upload-photo", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        uploaded.push(res.data.url);
      } catch (err) {
        toast.error(`${file.name}: ${errMsg(err, "errore")}`);
      }
    }
    setUploading(false);
    if (uploaded.length === 0) return;
    toast.success(`${uploaded.length} foto caricate nell'archivio`);
    if (multiple) {
      onSelect(uploaded);
    } else {
      onSelect(uploaded[0]);
    }
    onClose();
  };

  const filterChips = useMemo(() => {
    const chips = [];
    if (categories.length) chips.push({ kind: "category", label: "Categorie", values: categories });
    if (tags.length) chips.push({ kind: "tag", label: "Tag", values: tags.slice(0, 30) });
    return chips;
  }, [tags, categories]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="photo-picker-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Library className="w-5 h-5 text-[#1E3A5F]" />
            {title || (multiple ? "Scegli foto dall'archivio" : "Scegli una foto")}
          </DialogTitle>
        </DialogHeader>

        <div className="flex gap-2 border-b border-[#E8E2D9] mb-3">
          <button
            type="button"
            data-testid="photo-picker-tab-library"
            onClick={() => setTab("library")}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === "library" ? "border-[#AC5D45] text-[#AC5D45]" : "border-transparent text-[#635F59]"}`}
          >
            <ImageIcon className="w-4 h-4 inline mr-1" />Archivio
          </button>
          <button
            type="button"
            data-testid="photo-picker-tab-upload"
            onClick={() => setTab("upload")}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === "upload" ? "border-[#AC5D45] text-[#AC5D45]" : "border-transparent text-[#635F59]"}`}
          >
            <Upload className="w-4 h-4 inline mr-1" />Carica nuove
          </button>
        </div>

        {tab === "library" && (
          <div className="space-y-3">
            <div className="flex flex-wrap gap-2 items-center">
              <div className="relative flex-1 min-w-[220px]">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-[#635F59]" />
                <Input
                  placeholder="Cerca per titolo, tag o nome file…"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-8"
                  data-testid="photo-picker-search"
                />
              </div>
              {(activeTag || activeCategory) && (
                <Button variant="outline" size="sm" onClick={() => { setActiveTag(""); setActiveCategory(""); }}>
                  <X className="w-3 h-3 mr-1" />Reset filtri
                </Button>
              )}
            </div>

            {filterChips.map((group) => (
              <div key={group.kind} className="flex flex-wrap gap-1.5 items-center">
                <span className="text-[11px] uppercase tracking-wide text-[#635F59] font-semibold mr-1">
                  {group.label}
                </span>
                {group.values.map((v) => {
                  const active = group.kind === "tag" ? activeTag === v : activeCategory === v;
                  return (
                    <button
                      key={`${group.kind}-${v}`}
                      type="button"
                      data-testid={`photo-picker-${group.kind}-${v}`}
                      onClick={() => {
                        if (group.kind === "tag") setActiveTag(active ? "" : v);
                        else setActiveCategory(active ? "" : v);
                      }}
                      className={`text-xs px-2 py-0.5 rounded-full border ${active ? "bg-[#1E3A5F] text-white border-[#1E3A5F]" : "bg-white border-[#E8E2D9] text-[#1E3A5F] hover:border-[#AC5D45]"}`}
                    >
                      {v}
                    </button>
                  );
                })}
              </div>
            ))}

            <div className="text-xs text-[#635F59]">
              {loading ? "Caricamento…" : `${items.length} foto`}
              {selected.size > 0 && ` · ${selected.size} selezionate`}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2 max-h-[55vh] overflow-y-auto pr-1">
              {items.map((p) => {
                const isSel = selected.has(p.url);
                return (
                  <button
                    key={p.id}
                    type="button"
                    data-testid={`photo-picker-item-${p.id}`}
                    onClick={() => toggleSelect(p.url)}
                    className={`relative aspect-square rounded-lg overflow-hidden border-2 transition group ${isSel ? "border-[#AC5D45] ring-2 ring-[#AC5D45]/40" : "border-[#E8E2D9] hover:border-[#1E3A5F]"}`}
                  >
                    <img src={p.url} alt={p.title || p.original_filename || ""} className="w-full h-full object-cover" loading="lazy" />
                    {isSel && (
                      <span className="absolute top-1 right-1 bg-[#AC5D45] text-white text-[10px] font-bold px-1.5 py-0.5 rounded">
                        ✓
                      </span>
                    )}
                    {p.category && (
                      <span className="absolute bottom-1 left-1 bg-black/60 text-white text-[9px] px-1.5 py-0.5 rounded">
                        {p.category}
                      </span>
                    )}
                  </button>
                );
              })}
              {!loading && items.length === 0 && (
                <p className="col-span-full text-center text-[#635F59] py-8 text-sm">
                  Nessuna foto in archivio. Vai su "Carica nuove" per aggiungerne.
                </p>
              )}
            </div>
          </div>
        )}

        {tab === "upload" && (
          <div className="space-y-3" data-testid="photo-picker-upload-panel">
            <div>
              <Label>Titolo (opzionale)</Label>
              <Input value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} placeholder="es. Camera Mare al tramonto" className="mt-1" data-testid="photo-picker-upload-title" />
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              <div>
                <Label>Categoria</Label>
                <Input
                  value={uploadCategory}
                  onChange={(e) => setUploadCategory(e.target.value)}
                  placeholder="es. rooms, gallery, services…"
                  list="photo-categories"
                  className="mt-1"
                  data-testid="photo-picker-upload-category"
                />
                <datalist id="photo-categories">
                  {categories.map((c) => (<option key={c} value={c} />))}
                </datalist>
              </div>
              <div>
                <Label>Tag (separati da virgola)</Label>
                <Input
                  value={uploadTags}
                  onChange={(e) => setUploadTags(e.target.value)}
                  placeholder="es. mare, vista, tramonto"
                  className="mt-1"
                  data-testid="photo-picker-upload-tags"
                />
              </div>
            </div>
            <label className="flex flex-col items-center justify-center gap-2 h-40 rounded-lg border-2 border-dashed border-[#E8E2D9] cursor-pointer hover:border-[#AC5D45] hover:bg-[#FDFBF7] transition" data-testid="photo-picker-upload-dropzone">
              <input
                type="file"
                accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
                multiple
                className="hidden"
                disabled={uploading}
                onChange={(e) => {
                  const files = Array.from(e.target.files || []);
                  e.target.value = "";
                  handleUpload(files);
                }}
                data-testid="photo-picker-upload-input"
              />
              <Upload className="w-8 h-8 text-[#1E3A5F]" />
              <div className="text-sm font-semibold text-[#1E3A5F]">
                {uploading ? "Caricamento in corso…" : "Clicca o trascina le foto qui"}
              </div>
              <div className="text-[11px] text-[#635F59]">JPG · PNG · WEBP · HEIC — max 12 MB</div>
            </label>
            <p className="text-[11px] text-[#635F59]">
              💡 Le foto caricate qui finiscono nell'<strong>archivio centrale</strong> e potrai riusarle ovunque
              (camere, gallery, servizi) senza ricaricarle.
            </p>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="photo-picker-cancel">Annulla</Button>
          {tab === "library" && (
            <Button
              onClick={confirm}
              disabled={selected.size === 0}
              className="bg-[#AC5D45] hover:bg-[#8A4936] text-white"
              data-testid="photo-picker-confirm"
            >
              <Plus className="w-4 h-4 mr-1" />
              Usa {selected.size > 0 ? `(${selected.size})` : ""}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
